/**
 * 통합관리자 — 공통코드
 * ---------------------------------------------------------------------------
 * 상태·구분 값의 표시명과 색을 한곳에서 정한다. 화면마다 배열로 박아 두면
 * 같은 목록이 조금씩 달라지고, 표시명 하나를 바꾸려 해도 배포를 기다려야 한다.
 *
 * 화면에서 지키는 것
 *   - 시스템 코드는 **코드값**을 바꾸거나 지울 수 없다. DB 의 CHECK 제약과
 *     짝이라 코드값이 달라지면 화면에는 보이는데 저장은 실패한다.
 *     표시명·순서·색·활성 여부는 바꿀 수 있다.
 *   - 기업이 추가한 항목은 회색으로 보여 주되 고치지 않는다. 남의 회사 설정이다.
 *   - LOG_RETENTION 은 성격이 다르다. 표시가 아니라 동작(보관 일수)을 정하므로
 *     값 칸을 따로 안내하고 하한을 알려 준다.
 */

import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';

const { Text, Paragraph } = Typography;

/** 보관 기간 그룹. 값 칸의 뜻이 달라 화면이 따로 안내한다. */
const RETENTION_GROUP = 'LOG_RETENTION';

/** 태그 색. antd 기본 팔레트에서 실무에 쓸 만한 것만 추린다. */
const COLOR_OPTIONS = [
  { value: '', label: '기본' },
  { value: 'green', label: '초록 (정상)' },
  { value: 'blue', label: '파랑 (진행)' },
  { value: 'cyan', label: '청록' },
  { value: 'orange', label: '주황 (주의)' },
  { value: 'red', label: '빨강 (오류)' },
  { value: 'volcano', label: '진홍 (위험)' },
  { value: 'purple', label: '보라' },
];

interface CodeGroup {
  codeGroupId: string;
  groupCode: string;
  groupName: string;
  description: string | null;
  productCode: string | null;
  tenantExtensible: boolean;
  isSystem: boolean;
  isActive: boolean;
  itemCount: number;
  tenantItemCount: number;
}

interface CodeItem {
  codeItemId: string;
  itemCode: string;
  itemName: string;
  itemValue: string | null;
  extraAttrs: Record<string, unknown>;
  sortOrder: number;
  isSystem: boolean;
  isActive: boolean;
  isTenant: boolean;
  companyName: string | null;
}

export default function CodePage() {
  const { message } = AntdApp.useApp();
  const client = useQueryClient();
  const [groupForm] = Form.useForm();
  const [itemForm] = Form.useForm();

  const [includeInactive, setIncludeInactive] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [groupModal, setGroupModal] = useState<'create' | CodeGroup | null>(null);
  const [itemModal, setItemModal] = useState<'create' | CodeItem | null>(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const groups = useQuery<CodeGroup[], NormalizedError>({
    queryKey: ['admin', 'codes', includeInactive],
    queryFn: async () =>
      (await apiClient.get<CodeGroup[]>('/admin/codes', { params: { includeInactive } })).data,
  });

  // 처음 열면 첫 그룹을 고른다. 빈 오른쪽 칸을 보여 줄 이유가 없다.
  useEffect(() => {
    if (!selected && groups.data?.length) setSelected(groups.data[0]!.groupCode);
  }, [groups.data, selected]);

  const items = useQuery<CodeItem[], NormalizedError>({
    queryKey: ['admin', 'codes', selected, 'items'],
    enabled: Boolean(selected),
    queryFn: async () =>
      (await apiClient.get<CodeItem[]>(`/admin/codes/${selected}/items`)).data,
  });

  const group = groups.data?.find((g) => g.groupCode === selected) ?? null;
  const isRetention = selected === RETENTION_GROUP;

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['admin', 'codes'] });
    // 코드가 바뀌면 화면 곳곳의 표시명도 바뀐다.
    void client.invalidateQueries({ queryKey: ['codes'] });
  };

  /* ---- 그룹 저장 ---- */
  useEffect(() => {
    if (!groupModal) return;
    setFormError(null);
    groupForm.setFieldsValue(
      groupModal === 'create'
        ? { tenantExtensible: false, isActive: true }
        : { ...groupModal },
    );
  }, [groupModal, groupForm]);

  const saveGroup = async () => {
    const values = await groupForm.validateFields().catch(() => null);
    if (!values) return;

    setSaving(true);
    setFormError(null);
    try {
      if (groupModal === 'create') {
        await apiClient.post('/admin/codes', values);
        message.success('코드 그룹을 만들었습니다.');
      } else if (groupModal) {
        const { groupCode: _ignored, ...rest } = values;
        await apiClient.patch(`/admin/codes/${groupModal.groupCode}`, rest);
        message.success('코드 그룹을 수정했습니다.');
      }
      setGroupModal(null);
      refresh();
    } catch (err) {
      setFormError(errorText(err as NormalizedError));
    } finally {
      setSaving(false);
    }
  };

  /* ---- 항목 저장 ---- */
  useEffect(() => {
    if (!itemModal) return;
    setFormError(null);
    itemForm.setFieldsValue(
      itemModal === 'create'
        ? { sortOrder: 900, isActive: true, color: '' }
        : {
            ...itemModal,
            color: (itemModal.extraAttrs?.color as string) ?? '',
            days: isRetention ? Number(itemModal.itemValue ?? 0) : undefined,
          },
    );
  }, [itemModal, itemForm, isRetention]);

  const saveItem = async () => {
    const values = await itemForm.validateFields().catch(() => null);
    if (!values || !selected) return;

    const { color, days, ...rest } = values;
    const body = {
      ...rest,
      ...(isRetention ? { itemValue: String(days) } : {}),
      extraAttrs: { color: color || null },
    };

    setSaving(true);
    setFormError(null);
    try {
      if (itemModal === 'create') {
        await apiClient.post(`/admin/codes/${selected}/items`, body);
        message.success('코드를 추가했습니다.');
      } else if (itemModal) {
        // 시스템 코드는 코드값을 보내지 않는다. 서버도 막지만 화면이 먼저 막는다.
        const { itemCode, ...editable } = body;
        await apiClient.patch(
          `/admin/codes/${selected}/items/${itemModal.codeItemId}`,
          itemModal.isSystem ? editable : body,
        );
        message.success('코드를 수정했습니다.');
      }
      setItemModal(null);
      refresh();
    } catch (err) {
      setFormError(errorText(err as NormalizedError));
    } finally {
      setSaving(false);
    }
  };

  const removeItem = async (item: CodeItem) => {
    try {
      await apiClient.delete(`/admin/codes/${selected}/items/${item.codeItemId}`);
      message.success('코드를 지웠습니다.');
      refresh();
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    }
  };

  const itemColumns: ColumnsType<CodeItem> = [
    {
      title: '코드',
      dataIndex: 'itemCode',
      width: 180,
      render: (v: string, row) => (
        <Space size={4}>
          <Text code>{v}</Text>
          {row.isSystem && (
            <Tooltip title="DB 제약과 짝인 코드입니다. 코드값은 바꿀 수 없습니다.">
              <Tag>시스템</Tag>
            </Tooltip>
          )}
          {row.isTenant && <Tag color="blue">{row.companyName ?? '기업'}</Tag>}
        </Space>
      ),
    },
    {
      title: '표시명',
      dataIndex: 'itemName',
      render: (v: string, row) => {
        const color = row.extraAttrs?.color as string | undefined;
        return color ? <Tag color={color}>{v}</Tag> : <Text>{v}</Text>;
      },
    },
    {
      title: isRetention ? '보관 일수' : '값',
      dataIndex: 'itemValue',
      width: 140,
      render: (v: string | null, row) =>
        isRetention ? (
          <Space size={4}>
            <Text strong>{v}</Text>
            <Text type="secondary">일</Text>
            {Number(row.extraAttrs?.minDays ?? 0) > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                (하한 {String(row.extraAttrs.minDays)})
              </Text>
            )}
          </Space>
        ) : (
          (v ?? <Text type="secondary">—</Text>)
        ),
    },
    { title: '순서', dataIndex: 'sortOrder', width: 80 },
    {
      title: '상태',
      dataIndex: 'isActive',
      width: 90,
      render: (v: boolean) => (v ? <Tag color="green">사용</Tag> : <Tag>미사용</Tag>),
    },
    {
      title: '',
      width: 120,
      render: (_, row) =>
        row.isTenant ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            기업 항목
          </Text>
        ) : (
          <Space size={4}>
            <Button size="small" type="link" onClick={() => setItemModal(row)}>
              수정
            </Button>
            {!row.isSystem && (
              <Popconfirm
                title="이 코드를 지울까요?"
                description="이미 저장된 데이터가 이 값을 쓰고 있으면 화면에 코드값 그대로 보입니다."
                okText="삭제"
                cancelText="취소"
                onConfirm={() => void removeItem(row)}
              >
                <Button size="small" type="link" danger>
                  삭제
                </Button>
              </Popconfirm>
            )}
          </Space>
        ),
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Card
        size="small"
        title="공통코드"
        extra={
          <Space>
            <Checkbox
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            >
              미사용 포함
            </Checkbox>
            <Button size="small" icon={<PlusOutlined />} onClick={() => setGroupModal('create')}>
              그룹 추가
            </Button>
            <Button size="small" icon={<ReloadOutlined />} onClick={refresh} />
          </Space>
        }
      >
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          화면에 보이는 상태·구분 값의 표시명과 색을 정합니다. 시스템 코드의 코드값은 DB 제약과
          짝이라 바꿀 수 없고, 표시명·순서·색만 바꿉니다. 코드값 자체를 늘리려면 마이그레이션이
          필요합니다.
        </Paragraph>
      </Card>

      {groups.isError && <Alert type="error" showIcon message={errorText(groups.error)} />}

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <Card size="small" style={{ width: 280, flex: '0 0 280px' }} styles={{ body: { padding: 0 } }}>
          <Table<CodeGroup>
            rowKey="groupCode"
            size="small"
            showHeader={false}
            loading={groups.isLoading}
            pagination={false}
            dataSource={groups.data ?? []}
            scroll={{ y: 'calc(100vh - 320px)' }}
            rowClassName={(row) => (row.groupCode === selected ? 'ant-table-row-selected' : '')}
            onRow={(row) => ({
              onClick: () => setSelected(row.groupCode),
              style: { cursor: 'pointer' },
            })}
            columns={[
              {
                title: '그룹',
                render: (_, row) => (
                  <Space direction="vertical" size={0}>
                    <Space size={4}>
                      <Text strong={row.groupCode === selected}>{row.groupName}</Text>
                      {!row.isActive && <Tag>미사용</Tag>}
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {row.groupCode} · {row.itemCount}개
                    </Text>
                  </Space>
                ),
              },
            ]}
          />
        </Card>

        <Card
          size="small"
          style={{ flex: 1, minWidth: 0 }}
          title={group ? `${group.groupName} (${group.groupCode})` : '코드'}
          extra={
            group && (
              <Space>
                <Button size="small" onClick={() => setGroupModal(group)}>
                  그룹 수정
                </Button>
                <Button size="small" type="primary" onClick={() => setItemModal('create')}>
                  코드 추가
                </Button>
              </Space>
            )
          }
        >
          {group?.description && (
            <Paragraph type="secondary" style={{ marginBottom: 12 }}>
              {group.description}
            </Paragraph>
          )}
          {isRetention && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="이 그룹은 표시가 아니라 동작을 정합니다"
              description="적힌 일수가 지난 로그는 매일 새벽 3시 30분에 자동으로 삭제됩니다. 접속기록(감사로그·로그인)은 법정 보관 기간이 있어 하한 아래로 줄일 수 없습니다."
            />
          )}

          <Table<CodeItem>
            rowKey="codeItemId"
            size="small"
            loading={items.isFetching}
            pagination={false}
            dataSource={items.data ?? []}
            columns={itemColumns}
            locale={{ emptyText: '등록된 코드가 없습니다.' }}
          />
        </Card>
      </div>

      {/* ---- 그룹 ---- */}
      <Modal
        open={Boolean(groupModal)}
        title={groupModal === 'create' ? '코드 그룹 추가' : '코드 그룹 수정'}
        okText="저장"
        cancelText="취소"
        confirmLoading={saving}
        onCancel={() => setGroupModal(null)}
        onOk={() => void saveGroup()}
        destroyOnHidden
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {formError && <Alert type="error" showIcon message={formError} />}
          <Form form={groupForm} layout="vertical" requiredMark={false}>
            <Form.Item
              name="groupCode"
              label="그룹 코드"
              rules={[
                { required: true, message: '코드를 입력하세요' },
                {
                  pattern: /^[A-Z][A-Z0-9_]{1,59}$/,
                  message: '영문 대문자로 시작하는 2~60자 (대문자·숫자·밑줄)',
                },
              ]}
              extra={groupModal === 'create' ? undefined : '등록 후 바꿀 수 없습니다'}
            >
              <Input placeholder="ORDER_STATUS" disabled={groupModal !== 'create'} />
            </Form.Item>
            <Form.Item
              name="groupName"
              label="그룹 이름"
              rules={[{ required: true, message: '이름을 입력하세요' }]}
            >
              <Input placeholder="주문 상태" />
            </Form.Item>
            <Form.Item name="description" label="설명">
              <Input.TextArea rows={2} placeholder="어디에 쓰는 코드인지 적어 둡니다" />
            </Form.Item>
            <Form.Item
              name="tenantExtensible"
              valuePropName="checked"
              extra="기업이 자기 항목을 추가할 수 있게 합니다"
            >
              <Checkbox>기업 확장 허용</Checkbox>
            </Form.Item>
            <Form.Item name="isActive" valuePropName="checked">
              <Checkbox>사용</Checkbox>
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      {/* ---- 항목 ---- */}
      <Modal
        open={Boolean(itemModal)}
        title={itemModal === 'create' ? '코드 추가' : '코드 수정'}
        okText="저장"
        cancelText="취소"
        confirmLoading={saving}
        onCancel={() => setItemModal(null)}
        onOk={() => void saveItem()}
        destroyOnHidden
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {formError && <Alert type="error" showIcon message={formError} />}
          {itemModal !== 'create' && itemModal?.isSystem && (
            <Alert
              type="warning"
              showIcon
              message="시스템 코드입니다"
              description="코드값은 DB 제약과 짝이라 바꿀 수 없습니다. 표시명·순서·색만 바꿀 수 있습니다."
            />
          )}
          <Form form={itemForm} layout="vertical" requiredMark={false}>
            <Form.Item
              name="itemCode"
              label="코드"
              rules={[{ required: true, message: '코드를 입력하세요' }]}
            >
              <Input
                placeholder="PENDING"
                disabled={itemModal !== 'create' && Boolean(itemModal?.isSystem)}
              />
            </Form.Item>
            <Form.Item
              name="itemName"
              label="표시명"
              rules={[{ required: true, message: '표시명을 입력하세요' }]}
            >
              <Input placeholder="대기" />
            </Form.Item>

            {isRetention ? (
              <Form.Item
                name="days"
                label="보관 일수"
                rules={[{ required: true, message: '일수를 입력하세요' }]}
                extra={
                  itemModal !== 'create' && Number(itemModal?.extraAttrs?.minDays ?? 0) > 0
                    ? `${String(itemModal!.extraAttrs.minDays)}일 이상이어야 합니다 (법정 보관 기간)`
                    : '이 기간이 지난 기록은 매일 새벽에 삭제됩니다'
                }
              >
                <InputNumber min={1} max={36500} style={{ width: '100%' }} addonAfter="일" />
              </Form.Item>
            ) : (
              <Form.Item name="itemValue" label="값" extra="코드에 딸린 실제 값이 필요할 때만">
                <Input />
              </Form.Item>
            )}

            <Form.Item name="color" label="태그 색">
              <Select options={COLOR_OPTIONS} />
            </Form.Item>
            <Form.Item name="sortOrder" label="정렬 순서">
              <InputNumber min={0} max={9999} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item name="isActive" valuePropName="checked">
              <Checkbox>사용</Checkbox>
            </Form.Item>
          </Form>
        </Space>
      </Modal>
    </Space>
  );
}
